import { useImperativeHandle, useEffect, useState, useRef, forwardRef } from 'react'
import { useEditor, EditorContent, Editor } from '@tiptap/react'
import { Node } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableHeader from '@tiptap/extension-table-header'
import TableCell from '@tiptap/extension-table-cell'
import TextStyle from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
import FontFamily from '@tiptap/extension-font-family'
import Subscript from '@tiptap/extension-subscript'
import Superscript from '@tiptap/extension-superscript'
import Image from '@tiptap/extension-image'
import {
  Bold, Italic, Strikethrough, Code, Underline as UnderlineIcon,
  Heading1, Heading2, Heading3,
  List, ListOrdered, Quote,
  Link as LinkIcon, Unlink, Undo2, Redo2,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Table as TableIcon, Rows3, Columns3, Trash2,
  SeparatorHorizontal,
  Palette, Highlighter, Type, RemoveFormatting,
  Subscript as SubscriptIcon, Superscript as SuperscriptIcon,
  Image as ImageIcon,
} from 'lucide-react'

/**
 * Custom node: page break. In the editor it renders as a visible
 * placeholder bar. In the output HTML it carries CSS that both
 * WeasyPrint (for PDF) and LibreOffice (for DOCX) honour.
 */
const PageBreak = Node.create({
  name: 'pageBreak',
  group: 'block',
  atom: true,
  selectable: true,
  parseHTML() {
    return [{ tag: 'div[data-page-break]' }]
  },
  renderHTML() {
    return [
      'div',
      {
        'data-page-break': '',
        style:
          'page-break-after: always; break-after: page; ' +
          'border-top: 2px dashed #bbb; margin: 16pt 0; ' +
          'text-align: center; color: #888; font-size: 9pt; ' +
          'padding: 2px;',
      },
      'page break',
    ]
  },
  addCommands() {
    return {
      insertPageBreak:
        () =>
        ({ commands }: any) =>
          commands.insertContent({ type: this.name }),
    }
  },
})

export type TemplateEditorHandle = {
  insertText: (text: string) => void
  focus: () => void
}

interface Props {
  value: string
  onChange: (html: string) => void
  placeholder?: string
}

const TemplateEditor = forwardRef<TemplateEditorHandle, Props>(function TemplateEditor(
  { value, onChange, placeholder },
  ref,
) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Subscript,
      Superscript,
      Link.configure({ openOnClick: false, autolink: true }),
      Placeholder.configure({
        placeholder: placeholder || 'Write your template. Click variables in the sidebar to insert them.',
      }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Table.configure({ resizable: true, HTMLAttributes: { class: 'tpl-table' } }),
      TableRow, TableHeader, TableCell,
      // TextStyle is the carrier mark for inline attributes (color, fontFamily).
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      FontFamily,
      Image.configure({ inline: false, allowBase64: true }),
      PageBreak,
    ],
    content: value || '',
    immediatelyRender: false,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class:
          'ProseMirror prose prose-sm max-w-none min-h-[420px] p-4 outline-none focus:outline-none',
      },
    },
  })

  useImperativeHandle(ref, () => ({
    insertText: (text: string) => {
      if (!editor) return
      editor.chain().focus().insertContent(text).run()
    },
    focus: () => editor?.commands.focus(),
  }))

  useEffect(() => {
    if (!editor) return
    if (editor.isDestroyed) return
    if (value === editor.getHTML()) return
    editor.commands.setContent(value || '', false)
  }, [editor, value])

  if (!editor) {
    return (
      <div className="border rounded-md p-4 text-sm text-muted-foreground italic">
        Loading editor…
      </div>
    )
  }

  return (
    <div className="border rounded-md bg-background overflow-hidden">
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
      {/* Minimal table styling for the editor view (output HTML carries its own inline styles). */}
      <style>{`
        .ProseMirror table.tpl-table { border-collapse: collapse; margin: 4pt 0; width: 100%; }
        .ProseMirror table.tpl-table td, .ProseMirror table.tpl-table th { border: 1px solid #ddd; padding: 4pt 6pt; min-width: 40px; vertical-align: top; }
        .ProseMirror table.tpl-table th { background: #f4f6fa; font-weight: 600; }
        .ProseMirror .selectedCell { background: #e0e7ff; }
      `}</style>
    </div>
  )
})

export default TemplateEditor

// ─────────── Toolbar ───────────

const FONT_FAMILIES = [
  { value: '', label: 'Default' },
  { value: 'Inter, sans-serif', label: 'Inter' },
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: '"Times New Roman", serif', label: 'Times' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: '"Courier New", monospace', label: 'Courier' },
  { value: '"Helvetica Neue", sans-serif', label: 'Helvetica' },
]

const COLOR_PALETTE = [
  '#000000', '#374151', '#6B7280', '#9CA3AF',
  '#DC2626', '#EA580C', '#CA8A04', '#16A34A',
  '#0D9488', '#0284C7', '#4F46E5', '#9333EA',
]

const HIGHLIGHT_PALETTE = [
  '#FEF3C7', '#FED7AA', '#FECACA', '#FBCFE8',
  '#E9D5FF', '#C7D2FE', '#BAE6FD', '#A7F3D0',
]

function Toolbar({ editor }: { editor: Editor }) {
  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href as string | undefined
    const url = window.prompt('URL (leave blank to remove)', previousUrl || '')
    if (url === null) return
    if (url === '') {
      editor.chain().focus().unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }

  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
  }

  const insertImage = () => {
    const url = window.prompt('Image URL (https:// …)')
    if (!url) return
    editor.chain().focus().setImage({ src: url }).run()
  }

  const clearFormatting = () => {
    editor.chain().focus().unsetAllMarks().clearNodes().run()
  }

  const currentFontFamily = editor.getAttributes('textStyle').fontFamily || ''

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b bg-muted/20 px-2 py-1.5">
      {/* Font family */}
      <select
        value={currentFontFamily}
        onChange={(e) => {
          const v = e.target.value
          if (v) editor.chain().focus().setFontFamily(v).run()
          else editor.chain().focus().unsetFontFamily().run()
        }}
        className="h-7 text-xs bg-background border rounded px-1.5 mr-1"
        title="Font family"
      >
        {FONT_FAMILIES.map((f) => (
          <option key={f.value || 'default'} value={f.value}>{f.label}</option>
        ))}
      </select>
      <TbDivider />

      {/* Marks */}
      <TbBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold (Ctrl+B)"><Bold className="h-3.5 w-3.5" /></TbBtn>
      <TbBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic (Ctrl+I)"><Italic className="h-3.5 w-3.5" /></TbBtn>
      <TbBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline (Ctrl+U)"><UnderlineIcon className="h-3.5 w-3.5" /></TbBtn>
      <TbBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough"><Strikethrough className="h-3.5 w-3.5" /></TbBtn>
      <TbBtn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} title="Inline code"><Code className="h-3.5 w-3.5" /></TbBtn>
      <TbBtn onClick={() => (editor as any).chain().focus().toggleSubscript().run()} active={editor.isActive('subscript')} title="Subscript"><SubscriptIcon className="h-3.5 w-3.5" /></TbBtn>
      <TbBtn onClick={() => (editor as any).chain().focus().toggleSuperscript().run()} active={editor.isActive('superscript')} title="Superscript"><SuperscriptIcon className="h-3.5 w-3.5" /></TbBtn>
      <TbDivider />

      {/* Color + Highlight */}
      <ColorPicker
        icon={<Palette className="h-3.5 w-3.5" />}
        title="Text color"
        colors={COLOR_PALETTE}
        onPick={(c) => (c ? editor.chain().focus().setColor(c).run() : (editor as any).chain().focus().unsetColor().run())}
      />
      <ColorPicker
        icon={<Highlighter className="h-3.5 w-3.5" />}
        title="Highlight"
        colors={HIGHLIGHT_PALETTE}
        onPick={(c) => (c ? editor.chain().focus().setHighlight({ color: c }).run() : editor.chain().focus().unsetHighlight().run())}
      />
      <TbBtn onClick={clearFormatting} title="Clear formatting"><RemoveFormatting className="h-3.5 w-3.5" /></TbBtn>
      <TbDivider />
      {/* Headings */}
      <TbBtn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Heading 1"><Heading1 className="h-3.5 w-3.5" /></TbBtn>
      <TbBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2"><Heading2 className="h-3.5 w-3.5" /></TbBtn>
      <TbBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3"><Heading3 className="h-3.5 w-3.5" /></TbBtn>
      <TbDivider />
      {/* Alignment */}
      <TbBtn onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Align left"><AlignLeft className="h-3.5 w-3.5" /></TbBtn>
      <TbBtn onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Align center"><AlignCenter className="h-3.5 w-3.5" /></TbBtn>
      <TbBtn onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Align right"><AlignRight className="h-3.5 w-3.5" /></TbBtn>
      <TbBtn onClick={() => editor.chain().focus().setTextAlign('justify').run()} active={editor.isActive({ textAlign: 'justify' })} title="Justify"><AlignJustify className="h-3.5 w-3.5" /></TbBtn>
      <TbDivider />
      {/* Lists */}
      <TbBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list"><List className="h-3.5 w-3.5" /></TbBtn>
      <TbBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered list"><ListOrdered className="h-3.5 w-3.5" /></TbBtn>
      <TbBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Quote"><Quote className="h-3.5 w-3.5" /></TbBtn>
      <TbDivider />
      {/* Table */}
      <TbBtn onClick={insertTable} title="Insert 3×3 table"><TableIcon className="h-3.5 w-3.5" /></TbBtn>
      <TbBtn onClick={() => editor.chain().focus().addRowAfter().run()} disabled={!editor.can().addRowAfter()} title="Add row below"><Rows3 className="h-3.5 w-3.5" /></TbBtn>
      <TbBtn onClick={() => editor.chain().focus().addColumnAfter().run()} disabled={!editor.can().addColumnAfter()} title="Add column right"><Columns3 className="h-3.5 w-3.5" /></TbBtn>
      <TbBtn onClick={() => editor.chain().focus().deleteTable().run()} disabled={!editor.can().deleteTable()} title="Delete table"><Trash2 className="h-3.5 w-3.5" /></TbBtn>
      <TbDivider />
      {/* Link + Image */}
      <TbBtn onClick={setLink} active={editor.isActive('link')} title="Link"><LinkIcon className="h-3.5 w-3.5" /></TbBtn>
      <TbBtn onClick={() => editor.chain().focus().unsetLink().run()} disabled={!editor.isActive('link')} title="Unlink"><Unlink className="h-3.5 w-3.5" /></TbBtn>
      <TbBtn onClick={insertImage} title="Insert image from URL"><ImageIcon className="h-3.5 w-3.5" /></TbBtn>
      <TbDivider />
      {/* Page break */}
      <TbBtn
        onClick={() => (editor as any).chain().focus().insertPageBreak().run()}
        title="Insert page break"
      >
        <SeparatorHorizontal className="h-3.5 w-3.5" />
      </TbBtn>
      <TbDivider />
      {/* History */}
      <TbBtn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo"><Undo2 className="h-3.5 w-3.5" /></TbBtn>
      <TbBtn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo"><Redo2 className="h-3.5 w-3.5" /></TbBtn>
    </div>
  )
}

function TbBtn({
  children, onClick, active, disabled, title,
}: {
  children: React.ReactNode
  onClick: () => void
  active?: boolean
  disabled?: boolean
  title?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-1.5 rounded hover:bg-muted text-muted-foreground transition ${
        active ? 'bg-blue-100 text-blue-700' : ''
      } ${disabled ? 'opacity-40 cursor-not-allowed' : 'hover:text-foreground'}`}
    >
      {children}
    </button>
  )
}

function TbDivider() {
  return <span className="h-5 w-px bg-border mx-0.5" />
}

/** Small swatch picker used for text color + highlight. Click the icon
 * to open a popover with preset swatches and a "remove" option. */
function ColorPicker({
  icon, title, colors, onPick,
}: {
  icon: React.ReactNode
  title: string
  colors: string[]
  onPick: (c: string | null) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        title={title}
        onClick={() => setOpen((o) => !o)}
        className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
      >
        {icon}
      </button>
      {open && (
        <div className="absolute z-[60] top-full left-0 mt-1 p-2 rounded-md bg-popover shadow-md ring-1 ring-foreground/10 grid grid-cols-4 gap-1 w-40">
          {colors.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => { onPick(c); setOpen(false) }}
              className="w-6 h-6 rounded border border-foreground/10 hover:scale-110 transition-transform"
              style={{ backgroundColor: c }}
              title={c}
            />
          ))}
          <button
            type="button"
            onClick={() => { onPick(null); setOpen(false) }}
            className="col-span-4 text-xs text-muted-foreground hover:text-foreground border rounded px-2 py-1 mt-1"
          >
            Remove
          </button>
        </div>
      )}
    </div>
  )
}
